import { useState } from 'react'
import { X, Plus, ShieldBan, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { addBlockedSender, removeBlockedSender } from '../blocked-senders'

interface BlockedSendersContent {
  type: 'blocked-senders'
  entries: string[]
}

interface Props {
  content: BlockedSendersContent
}

export default function BlockedSendersExpanded({ content }: Props) {
  const { t } = useTranslation('settings')
  const [entries, setEntries] = useState<string[]>(content.entries)
  const [newEntry, setNewEntry] = useState('')
  const [saving, setSaving] = useState(false)
  const [removingEntry, setRemovingEntry] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const handleAdd = async () => {
    const trimmed = newEntry.trim().toLowerCase()
    if (!trimmed || entries.includes(trimmed)) return
    setSaving(true)
    setError(null)
    try {
      const updated = await addBlockedSender(trimmed)
      setEntries(updated)
      setNewEntry('')
    } catch (err) {
      setError(err instanceof Error ? err.message : t('blockedSenders.failedToAdd'))
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (entry: string) => {
    setRemovingEntry(entry)
    setError(null)
    try {
      const updated = await removeBlockedSender(entry)
      setEntries(updated)
    } catch (err) {
      setError(err instanceof Error ? err.message : t('blockedSenders.failedToRemove'))
    } finally {
      setRemovingEntry(null)
    }
  }

  return (
    <div className="space-y-2">
      <p className="text-xs text-pearl/50">
        {t('blockedSenders.description')}
      </p>

      {error && (
        <p className="text-xs text-rose/70">{error}</p>
      )}

      {/* Blocked entries list */}
      {entries.length === 0 && (
        <p className="text-xs text-pearl/55 py-1">{t('blockedSenders.empty')}</p>
      )}
      {entries.map((entry) => {
        const isRemoving = removingEntry === entry
        return (
          <div key={entry} className={`flex items-center justify-between py-1.5 ${isRemoving ? 'opacity-50' : ''}`}>
            <div className="flex items-center gap-2 min-w-0">
              <ShieldBan size={14} className="text-rose/40 shrink-0" />
              <span className="text-sm text-pearl truncate">{entry}</span>
            </div>
            {isRemoving ? (
              <Loader2 size={14} className="text-pearl/55 shrink-0 animate-spin" />
            ) : (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleRemove(entry)
                }}
                disabled={!!removingEntry}
                className="p-1 rounded hover:bg-rose/10 transition-colors shrink-0 disabled:opacity-50"
              >
                <X size={14} className="text-pearl/60 hover:text-rose" />
              </button>
            )}
          </div>
        )
      })}

      {/* Add entry input */}
      <div className="flex items-center gap-2 pt-1">
        <input
          type="text"
          value={newEntry}
          onChange={(e) => setNewEntry(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd() }}
          onClick={(e) => e.stopPropagation()}
          placeholder={t('blockedSenders.addPlaceholder')}
          disabled={saving}
          className="flex-1 bg-slate-mid/30 rounded-lg px-3 py-1.5 text-sm text-ivory placeholder:text-pearl/55 outline-none focus:ring-1 focus:ring-rose/40 disabled:opacity-50"
        />
        <button
          onClick={(e) => {
            e.stopPropagation()
            handleAdd()
          }}
          disabled={saving}
          className="p-1.5 rounded-lg bg-rose/10 text-rose hover:bg-rose/20 transition-colors disabled:opacity-50"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
        </button>
      </div>
    </div>
  )
}
